import React from 'react';

const formatNum = (n) => (n || 0).toLocaleString();

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

const STATUS = {
  empty: {
    label: 'Out of credits',
    classes: 'bg-red-500/10 text-red-600 dark:text-red-400 ring-1 ring-red-500/20',
    dot: 'bg-red-500',
    pulse: true,
  },
  low: {
    label: 'Running low',
    classes: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500/20',
    dot: 'bg-amber-400',
    pulse: true,
  },
};

const TONE = {
  neutral: 'text-gray-900 dark:text-gray-100',
  warning: 'text-amber-600 dark:text-amber-400',
  positive: 'text-emerald-600 dark:text-emerald-400',
};

const Stat = ({ label, value, tone = 'neutral' }) => (
  <div className="min-w-0">
    <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 whitespace-nowrap">
      {label}
    </dt>
    <dd className={`mt-1 text-base font-bold tabular-nums whitespace-nowrap ${TONE[tone]}`}>{value}</dd>
  </div>
);

// The page-level balance panel on /topups: how much is left, how fast it is
// going, and when the plan tops itself up again — the context that makes a
// pack size feel like a decision rather than a guess.
const BalanceCard = ({ credits, planLabel, nextCreditReplenishment }) => {
  if (!credits) return null;

  const available = credits.available || 0;
  const consumed = credits.consumed || 0;
  const total = credits.total || 0;
  const expiringSoon = credits.expiring_soon || 0;
  const pct = total > 0 ? Math.min((available / total) * 100, 100) : 0;
  const status = available === 0 ? STATUS.empty : pct < 20 ? STATUS.low : null;
  const renewsOn = formatDate(nextCreditReplenishment);

  return (
    <section className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700/30 bg-white dark:bg-gray-900/40 p-6 sm:p-7 shadow-sm">
      <div className="pointer-events-none absolute -top-24 -right-16 w-64 h-64 rounded-full bg-blue-500/[0.06] dark:bg-blue-500/[0.07] blur-3xl" />

      <div className="relative flex flex-col md:flex-row md:flex-wrap md:items-center md:justify-between gap-6">
        {/* Balance */}
        <div className="min-w-0 md:flex-shrink-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              Current balance
            </p>
            {status && (
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${status.classes}`}>
                <span className="relative flex h-1.5 w-1.5">
                  {status.pulse && (
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${status.dot}`} />
                  )}
                  <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${status.dot}`} />
                </span>
                {status.label}
              </span>
            )}
          </div>
          <p className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight tabular-nums text-gray-900 dark:text-white leading-none whitespace-nowrap">
            {formatNum(available)}
            <span className="ml-2 text-lg sm:text-xl font-medium text-gray-400 dark:text-gray-500">
              / {formatNum(total)}
            </span>
          </p>
          <p className="mt-2.5 text-sm text-gray-500 dark:text-gray-400">
            credits available on{' '}
            <span className="font-semibold bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent">
              {planLabel}
            </span>
          </p>
        </div>

        {/* Secondary stats */}
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-4 md:ml-auto md:flex-shrink-0">
          <Stat label="Consumed" value={formatNum(consumed)} />
          <Stat
            label="Expiring in 7 days"
            value={formatNum(expiringSoon)}
            tone={expiringSoon > 0 ? 'warning' : 'neutral'}
          />
          {renewsOn && <Stat label="Plan credits renew" value={renewsOn} tone="positive" />}
        </dl>
      </div>

      {/* Progress */}
      <div className="relative mt-6 h-2.5 w-full rounded-full bg-gray-100 dark:bg-white/[0.06] overflow-hidden ring-1 ring-inset ring-gray-200/60 dark:ring-white/[0.04]">
        <div
          className="h-full rounded-full bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 shadow-[0_0_10px_rgba(34,211,238,0.35)] transition-all duration-700"
          style={{ width: `${pct.toFixed(1)}%` }}
        />
      </div>
    </section>
  );
};

export default BalanceCard;
